"use client";

import React, { createContext, useContext, ReactNode, useState } from "react";
import { Widget } from "../types";

interface EditWidgetContextType {
    isOpen: boolean;
    widget: Widget | null;
    isDuplicating: boolean;
    isSharedClone: boolean;
    openEditDrawer: (widget: Widget) => void;
    openDuplicateDrawer: (widget: Widget, sharedClone?: boolean) => void;
    closeEditDrawer: () => void;
}

const EditWidgetContext = createContext<EditWidgetContextType | null>(null);
EditWidgetContext.displayName = "EditWidgetContext";

interface EditWidgetProviderProps {
    children: ReactNode;
}

export const EditWidgetProvider: React.FC<EditWidgetProviderProps> = ({ children }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [widget, setWidget] = useState<Widget | null>(null);
    const [isDuplicating, setIsDuplicating] = useState(false);
    const [isSharedClone, setIsSharedClone] = useState(false);

    const openEditDrawer = (widgetToEdit: Widget) => {
        setIsDuplicating(false);
        setIsSharedClone(false);
        setWidget(widgetToEdit);
        setIsOpen(true);
    };

    const openDuplicateDrawer = (widgetToDuplicate: Widget, sharedClone = false) => {
        setIsDuplicating(true);
        setIsSharedClone(sharedClone);
        setWidget(widgetToDuplicate);
        setIsOpen(true);
    };

    const closeEditDrawer = () => {
        setIsOpen(false);
        setWidget(null);
        setIsDuplicating(false);
        setIsSharedClone(false);
    };

    return <EditWidgetContext.Provider value={{ isOpen, widget, isDuplicating, isSharedClone, openEditDrawer, openDuplicateDrawer, closeEditDrawer }}>{children}</EditWidgetContext.Provider>;
};

export const useEditWidget = () => {
    const context = useContext(EditWidgetContext);
    if (!context) {
        throw new Error("useEditWidget must be used within an EditWidgetProvider");
    }
    return context;
};
